import { useEffect, useMemo, useState } from 'react'
import { View } from 'react-native'
import { Redirect, useLocalSearchParams, usePathname, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { MapPin } from '@/components/ui/icons'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import { PreviewFixtureRedirect } from '@/components/navigation/PreviewFixtureRedirect'
import { env } from '@/lib/env'
import { isIsolatedPreviewBuild } from '@/lib/runtime-build'
import { useTourExperience, useReserveTour, useTourReviews } from '@/lib/hooks/use-tours'
import { useToursCheckoutStore } from '@/lib/stores/tours-checkout.store'
import { useAuth } from '@/lib/hooks/use-auth'
import { getContactVerificationRequirement } from '@/lib/utils/account-contact'
import { TourExperienceDetailView } from '@/components/tours/TourExperienceDetailView'
import type { SearchLocation } from '@/components/location/LocationSearchModal'
import { useTheme } from '@/lib/hooks/use-theme'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import { useSemanticColors } from '@/lib/theme/tokens'
import { SPACING, RADIUS } from '@/lib/brand'
import {
  catalogCanvasPadX,
  tourDetailCoverAspect,
  tourDetailCoverMaxHeight,
  tourDetailTwoColumn,
} from '@/lib/tours/catalog-layout'
import { earliestTourBookingDateISO, MIN_TOUR_PASSENGERS } from '@/lib/tours/booking-rules'
import { toursPerPersonFromVehicleCents } from '@/lib/tours/pricing'
import { parseTourDescription } from '@/lib/tours/tariffs'
import { toursPaymentErrorKey } from '@/lib/tours/payment-errors'
import { findCountry } from '@/lib/data/countries'
import { toBookingDateTimeISO } from '@/lib/utils/dates'
import type {
  TourExperienceDetail,
  TourLuggageCode,
  TourReserveInput,
  TourReserveResult,
  TourVehicleOption,
} from '@/lib/types/tours.types'

function DetailContent({
  experience,
  reserveOverride,
}: {
  experience: TourExperienceDetail
  reserveOverride?: (input: TourReserveInput) => Promise<TourReserveResult>
}) {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const pathname = usePathname() ?? ''
  const { isDesktop, isTablet, width } = useBreakpoint()
  const editorial = isDesktop || isTablet
  const twoColumn = tourDetailTwoColumn(editorial, width)
  const padX = catalogCanvasPadX()
  const catalogFallback = pathname.startsWith('/preview') ? '/preview' : '/(tabs)/tours'

  const vehicles = useMemo(
    () => experience.vehicles.filter((vehicle) => vehicle.seats >= MIN_TOUR_PASSENGERS),
    [experience.vehicles],
  )
  const earliestBookableDate = useMemo(() => earliestTourBookingDateISO(), [])
  const customQuote = experience.customQuote ?? null
  const quotedPassengerCount = useMemo(() => {
    if (!customQuote || !Number.isSafeInteger(customQuote.passengerCount)) return null
    const vehicleCapacity = vehicles[0]?.seats ?? 0
    if (
      customQuote.passengerCount < MIN_TOUR_PASSENGERS ||
      customQuote.passengerCount > vehicleCapacity
    ) {
      return null
    }
    return customQuote.passengerCount
  }, [customQuote, vehicles])
  const [quoteNowMs, setQuoteNowMs] = useState(() => Date.now())
  const customQuoteExpiresAtMs = customQuote ? Date.parse(customQuote.expiresAt) : Number.NaN

  useEffect(() => {
    if (!Number.isFinite(customQuoteExpiresAtMs)) return undefined
    const delay = Math.max(0, customQuoteExpiresAtMs - Date.now()) + 20
    const timer = setTimeout(() => setQuoteNowMs(Date.now()), delay)
    return () => clearTimeout(timer)
  }, [customQuoteExpiresAtMs])

  const customQuoteExpired =
    customQuote !== null &&
    (customQuote.isExpired ||
      !Number.isFinite(customQuoteExpiresAtMs) ||
      customQuoteExpiresAtMs <= quoteNowMs)

  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(
    vehicles[0]?.unitId ?? null,
  )
  const [date, setDate] = useState<string | null>(earliestBookableDate)
  const [time, setTime] = useState<string | null>('09:00')
  const [passengerCount, setPassengerCount] = useState(quotedPassengerCount ?? MIN_TOUR_PASSENGERS)
  const [luggageCodes, setLuggageCodes] = useState<TourLuggageCode[]>([])
  const [startPoint, setStartPoint] = useState<SearchLocation | null>(null)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [consentError, setConsentError] = useState<string | null>(null)
  const [reserveErrorKey, setReserveErrorKey] = useState<string | null>(null)
  const reserve = useReserveTour()
  const setCheckoutContext = useToursCheckoutStore((s) => s.setContext)
  const reviews = useTourReviews(experience.id)
  const { user, profile } = useAuth()
  // Logged-out discovery must lead to sign-in, not imply that a guest has an
  // account contact to verify. The reservation endpoint remains authoritative
  // once the user is signed in.
  const contactRequirement = user ? getContactVerificationRequirement(user, profile) : null
  const isAuthenticated = Boolean(user)

  const selectedVehicle =
    vehicles.find((v) => v.unitId === selectedVehicleId) ?? vehicles[0] ?? null
  const maxPassengers = Math.max(MIN_TOUR_PASSENGERS, ...vehicles.map((vehicle) => vehicle.seats))
  const requiresConsent = Boolean(experience.travelInfo?.meetingPoint)

  useEffect(() => {
    if (quotedPassengerCount !== null) {
      setPassengerCount(quotedPassengerCount)
      return
    }
    setPassengerCount((count) => Math.min(Math.max(count, MIN_TOUR_PASSENGERS), maxPassengers))
  }, [maxPassengers, quotedPassengerCount])

  useEffect(() => {
    if (customQuote) {
      const quotedVehicle = vehicles[0]
      if (quotedVehicle && selectedVehicleId !== quotedVehicle.unitId) {
        setSelectedVehicleId(quotedVehicle.unitId)
      }
      return
    }
    const currentVehicle = vehicles.find((vehicle) => vehicle.unitId === selectedVehicleId)
    if (currentVehicle && currentVehicle.seats >= passengerCount) return

    const smallestCompatibleVehicle = vehicles
      .filter((vehicle) => vehicle.seats >= passengerCount)
      .reduce<TourVehicleOption | null>(
        (smallest, vehicle) => (!smallest || vehicle.seats < smallest.seats ? vehicle : smallest),
        null,
      )
    if (smallestCompatibleVehicle) setSelectedVehicleId(smallestCompatibleVehicle.unitId)
  }, [customQuote, passengerCount, selectedVehicleId, vehicles])

  const perPersonCents = selectedVehicle
    ? toursPerPersonFromVehicleCents(selectedVehicle.guestPriceCents, passengerCount)
    : 0

  const hasMinimumLeadTime = date !== null && date >= earliestBookableDate
  const isReserveDisabled =
    !selectedVehicle ||
    !date ||
    !time ||
    passengerCount < MIN_TOUR_PASSENGERS ||
    (customQuote !== null && (quotedPassengerCount === null || customQuoteExpired)) ||
    !hasMinimumLeadTime ||
    (!reserveOverride && reserve.isPending)

  const startReservation = async () => {
    if (!selectedVehicle || !date || !time || customQuoteExpired) return
    const startsAt = toBookingDateTimeISO(date, time)
    setReserveErrorKey(null)
    if (requiresConsent && !termsAccepted) {
      setConsentError(t('tours.travel_info.consent_required'))
      return
    }
    setConsentError(null)
    if (date < earliestBookableDate) {
      setReserveErrorKey('tours.errors.minimum_lead_time')
      return
    }
    try {
      const idempotencyKey = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
      const reservationInput: TourReserveInput = {
        idempotencyKey,
        experienceId: experience.id,
        unitId: selectedVehicle.unitId,
        startsAt,
        passengerCount: Math.min(Math.max(passengerCount, MIN_TOUR_PASSENGERS), maxPassengers),
        luggageSpec: luggageCodes.slice(0, 8),
        termsAccepted: true,
        // Un tour cotizado ya nació con su origen; el catálogo manda el punto
        // elegido y `null` conserva el punto de encuentro oficial.
        startPoint: customQuote
          ? null
          : startPoint
            ? {
                label: startPoint.name?.trim() || startPoint.label,
                address: startPoint.address?.trim() || startPoint.label,
                latitude: startPoint.latitude,
                longitude: startPoint.longitude,
              }
            : null,
      }
      const result = reserveOverride
        ? await reserveOverride(reservationInput)
        : await reserve.mutateAsync(reservationInput)
      setCheckoutContext({
        booking: result.booking,
        title: experience.title,
        originLabel: experience.originLabel,
        destinationLabel: experience.destinationLabel,
        startsAt: result.booking.startsAt ?? startsAt,
        luggageLabels: luggageCodes.map((code) => t(`tours.luggage_${code}`)),
        coverImageUrl: experience.coverImageUrl,
        customTour:
          customQuote && selectedVehicle
            ? { vehicleLabel: selectedVehicle.label, stopCount: experience.stops.length }
            : undefined,
      })
      router.push('/(tabs)/tours/checkout')
    } catch (err) {
      setReserveErrorKey(toursPaymentErrorKey((err as { code?: string }).code))
    }
  }

  const hours = Math.floor(experience.durationMinutes / 60)
  const minutes = experience.durationMinutes % 60
  const durationLabel =
    hours > 0 ? `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}` : `${experience.durationMinutes}m`
  const routeLabel = t('tours.route', {
    origin: experience.originLabel,
    destination: experience.destinationLabel,
  })
  const { body: descriptionBody } = parseTourDescription(experience.description)
  const destinationCountry = experience.destinationCountryCode
    ? findCountry(experience.destinationCountryCode)
    : undefined
  const destinationName = destinationCountry
    ? i18n.language.startsWith('en')
      ? destinationCountry.nameEn
      : destinationCountry.nameEs
    : null

  return (
    <TourExperienceDetailView
      experience={experience}
      editorial={editorial}
      twoColumn={twoColumn}
      padX={padX}
      catalogFallback={catalogFallback}
      routeLabel={routeLabel}
      durationLabel={durationLabel}
      descriptionBody={descriptionBody}
      destinationName={destinationName}
      selectedVehicle={selectedVehicle}
      vehicles={vehicles}
      date={date}
      time={time}
      earliestBookableDate={earliestBookableDate}
      passengerCount={passengerCount}
      maxPassengers={maxPassengers}
      luggageCodes={luggageCodes}
      startPoint={startPoint}
      termsAccepted={requiresConsent ? termsAccepted : true}
      consentError={consentError}
      reserveErrorKey={reserveErrorKey}
      perPersonCents={perPersonCents}
      isReserveDisabled={isReserveDisabled}
      reservePending={reserveOverride ? false : reserve.isPending}
      isAuthenticated={isAuthenticated}
      contactRequirement={contactRequirement}
      reviewsLoading={reviews.isLoading}
      reviews={reviews.data}
      onBack={() => router.back()}
      onDateChange={setDate}
      onTimeChange={setTime}
      onPassengerCount={setPassengerCount}
      onSelectVehicle={setSelectedVehicleId}
      onToggleLuggage={(code) =>
        setLuggageCodes((current) =>
          current.includes(code) ? current.filter((item) => item !== code) : [...current, code],
        )
      }
      onStartPointChange={setStartPoint}
      onTermsChange={(checked) => {
        setTermsAccepted(checked)
        if (checked) setConsentError(null)
      }}
      onReserve={() => void startReservation()}
      onVerify={() => {
        if (!user) {
          router.push('/(auth)/login')
          return
        }
        router.push(
          contactRequirement === 'phone' ? '/(auth)/verify-phone' : '/(auth)/verify-email',
        )
      }}
      onCreateAnotherQuote={() =>
        router.replace(pathname.startsWith('/preview') ? '/preview/quote' : '/tours/quote')
      }
    />
  )
}

function TourExperienceDataScreen({
  experienceId,
  reserveOverride,
}: {
  experienceId?: string
  reserveOverride?: (input: TourReserveInput) => Promise<TourReserveResult>
}) {
  const { data, isLoading, error, refetch } = useTourExperience(experienceId ?? '')
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { isDesktop, isTablet } = useBreakpoint()
  const editorial = isDesktop || isTablet

  if (!env.isToursEnabled) {
    return <Redirect href="/(tabs)/explore" />
  }

  if (!experienceId || error) {
    return (
      <ScreenContainer variant={editorial ? 'full-bleed' : undefined}>
        <View style={{ flex: 1, justifyContent: 'center' }}>
          <EmptyState
            illustration={<MapPin size={44} color={colors.textMuted} strokeWidth={1.5} />}
            title={t('tours.detail_error_title')}
            description={t('tours.detail_error_description')}
            action={{ label: t('tours.retry'), onPress: () => void refetch() }}
          />
        </View>
      </ScreenContainer>
    )
  }

  if (isLoading || !data) {
    return (
      <ScreenContainer variant={editorial ? 'full-bleed' : undefined}>
        <View style={{ paddingHorizontal: SPACING.lg, paddingTop: SPACING.lg }}>
          <Skeleton width="55%" height={26} />
          <View
            style={{
              marginTop: SPACING.md,
              width: '100%',
              aspectRatio: tourDetailCoverAspect(editorial),
              maxHeight: tourDetailCoverMaxHeight(editorial),
            }}
          >
            <Skeleton width="100%" height="100%" borderRadius={RADIUS.xl} />
          </View>
          <View style={{ marginTop: SPACING.lg, gap: SPACING.sm }}>
            <Skeleton width="70%" height={24} />
            <Skeleton width="90%" height={15} />
            <Skeleton width="40%" height={15} />
            <Skeleton width="80%" height={14} />
            <Skeleton width="95%" height={14} />
          </View>
        </View>
      </ScreenContainer>
    )
  }

  if (!data.vehicles.some((vehicle) => vehicle.seats >= MIN_TOUR_PASSENGERS)) {
    return (
      <ScreenContainer variant={editorial ? 'full-bleed' : undefined}>
        <View style={{ flex: 1, justifyContent: 'center' }}>
          <EmptyState
            illustration={<MapPin size={44} color={colors.textMuted} strokeWidth={1.5} />}
            title={t('tours.detail_error_title')}
            description={t('tours.minimum_group_unavailable', {
              count: MIN_TOUR_PASSENGERS,
            })}
            action={{ label: t('tours.retry'), onPress: () => void refetch() }}
          />
        </View>
      </ScreenContainer>
    )
  }

  return <DetailContent experience={data} reserveOverride={reserveOverride} />
}

/**
 * Keep the redirect boundary separate from the data screen. A Preview task can
 * replace /tours/:id with /preview/:id in place during Fast Refresh; putting
 * the data hooks below that conditional in the same component would change
 * hook order between renders.
 */
export default function TourExperienceScreen({
  reserveOverride,
}: {
  reserveOverride?: (input: TourReserveInput) => Promise<TourReserveResult>
} = {}) {
  const params = useLocalSearchParams<{ experienceId?: string | string[] }>()
  const pathname = usePathname() ?? ''
  const experienceId = Array.isArray(params.experienceId)
    ? params.experienceId[0]
    : params.experienceId

  // A Preview install can retain an old normal Tours deep link after Fast
  // Refresh or an Android task restore. Resolve it before any data or mutation
  // hook mounts, preserving the ID but moving into the fixture-only route.
  if (isIsolatedPreviewBuild && !pathname.startsWith('/preview')) {
    return <PreviewFixtureRedirect href={experienceId ? `/preview/${experienceId}` : '/preview'} />
  }

  return <TourExperienceDataScreen experienceId={experienceId} reserveOverride={reserveOverride} />
}
